import MutableHamburgerButton from '@onepercentio/one-ui/components/MutableHamburgerButton'
import DropStepper from 'components/DropStepper'
import {
  ActionStep,
  MultiStepOperation,
  StaticStep,
} from 'components/MultiStepOperation/MultiStepOperation.view'
import { AssetType, Sale } from 'core/logic/asset/asset.types'
import {
  ComponentProps,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import { t } from 'translate/i18n'
import { OfferForm } from '../CreateOffer.types'
import ProgressBar from '@onepercentio/one-ui/components/ProgressBar'
import Styles from './Confirm.module.scss'
import Spacing from '@onepercentio/one-ui/components/Spacing'
import Text from 'openspace/components/Text'
import { LPTypo } from 'containers/OneUIProvider/OneUIProvider.data'
import Button from '@onepercentio/one-ui/components/Button'
import IconRarum from 'components/IconRarum'
import useAsyncControl from '@onepercentio/one-ui/hooks/useAsyncControl'
import AsyncControl from 'components/AsyncControl'
import { useProgressUI } from 'openspace/service/Progress'

export enum CONFIRMATION_STATES {
  CHECKING_APPROVAL, // Checking if the user already approved the nfts movement
  READY_FOR_CONFIRMING, // User already approved and now is waiting for the user to confirm sale creation
}

export default function MultiStepConfirmation({
  offer,
  state,
  loading,
  error,
  asset,
  onApprove,
  onCreate,
  onGoBack,
  onGoToOffer,
  onRetry,
  offerId,
  rootRef,
}: {
  offer: OfferForm & { offerId?: string }
  state: CONFIRMATION_STATES
  loading: boolean
  error: boolean
  onApprove: () => void
  onCreate: () => void
  onGoBack: () => void
  onGoToOffer: () => void
  onRetry: () => void
  asset: AssetType
  offerId?: Sale['offerId']
  rootRef?: RefObject<HTMLDivElement>
}) {
  const currentStep = useMemo(() => {
    switch (state) {
      case CONFIRMATION_STATES.CHECKING_APPROVAL:
        return 2
      case CONFIRMATION_STATES.READY_FOR_CONFIRMING:
        return 3
    }
  }, [state])
  const status = useMemo<
    ComponentProps<typeof MutableHamburgerButton>['state']
  >(() => {
    return loading ? 'loading' : error ? 'closed' : 'checkmark'
  }, [loading, error])
  return (
    <>
      <DropStepper
        rootRef={rootRef}
        steps={
          <MultiStepOperation currentStep={currentStep}>
            <StaticStep
              stepNum={1}
              stepName={t('marketplace.offerCreation.confirmation.configure')}
            />
            <ActionStep
              stepNum={2}
              stepName={t('marketplace.offerCreation.confirmation.approve')}
              error={currentStep === 2 && error}
              status={
                currentStep === 2
                  ? status
                  : currentStep === 3
                  ? 'checkmark'
                  : 'arrow-down'
              }
              action={{
                label: t(
                  currentStep === 2 && error
                    ? 'generic.retryBtn'
                    : 'marketplace.offerCreation.confirmation.actions.approve'
                ),
                onClick: error ? onRetry : onApprove,
                disabled: loading || currentStep !== 2,
              }}
            />
            <ActionStep
              stepNum={3}
              stepName={t('marketplace.offerCreation.confirmation.confirm')}
              error={currentStep === 3 && error}
              status={currentStep === 3 ? status : 'arrow-down'}
              action={{
                label: t(
                  currentStep === 3 && error
                    ? 'generic.retryBtn'
                    : offerId
                    ? 'marketplace.offerCreation.creation.finished.actions.seeItem'
                    : 'marketplace.offerCreation.confirmation.actions.confirm'
                ),
                onClick: error ? onRetry : offerId ? onGoToOffer : onCreate,
                disabled: loading || currentStep !== 3,
              }}
            />
          </MultiStepOperation>
        }
        asset={asset}
        assetInfo={{
          currency: Object.keys(offer.amount)[0],
          price: Object.values(offer.amount)[0]!.amount,
          quantity: offer.selectedQuantity,
        }}
        label={{
          back: t('marketplace.offerCreation.actions.backToHome'),
          title: t('marketplace.offerCreation.confirmation.title'),
          description: t('marketplace.offerCreation.confirmation.description'),
          cancel: t('marketplace.offerCreation.confirmation.actions.cancel'),
          preview: t('marketplace.offerCreation.sections.preview.title'),
        }}
        loading={loading}
        onBack={onGoBack}
      />
    </>
  )
}

export enum AutoConfirmationStatus {
  LOADING,
  NEED_ALLOWANCE,
  NEED_CONFIRMATION,
  CONFIRMED,
}
export function AutoConfirmation({
  status,
  onApproveAllowance,
  onConfirmOperation,
  label,
  onGoToItem,
  onCancel,
  progressLabel,
}: {
  status: AutoConfirmationStatus
  label: {
    loading: TranslationCodes
    confirmed?: {
      title: TranslationCodes
      description: TranslationCodes
    }
  }
  progressLabel: null | ReturnType<typeof useProgressUI>
} & ClickActions<'onApproveAllowance' | 'onConfirmOperation', Promise<void>> &
  ClickActions<'onCancel'> &
  Partial<ClickActions<'onGoToItem'>>) {
  const doAction = () => {
    switch (status) {
      case AutoConfirmationStatus.NEED_ALLOWANCE:
        return onApproveAllowance()
      case AutoConfirmationStatus.NEED_CONFIRMATION:
        return onConfirmOperation()
    }
  }
  const control = useAsyncControl()
  useEffect(() => {
    control.process(() => doAction()!)
  }, [status])

  if (status === AutoConfirmationStatus.CONFIRMED)
    if (label.confirmed)
      return (
        <>
          <div className={Styles.root}>
            <div className={Styles.root}>
              <IconRarum
                icon='roundCheckmark'
                size={120}
                color='var(--ok-color)'
              />
              <Text code={label.confirmed.title} type={LPTypo.H5} />
              <Spacing size='40' />
              <Text code={label.confirmed.description} type={LPTypo.P2} />
            </div>
            <Spacing size='stretch' />
            <Button onClick={onGoToItem} variant='filled'>
              {t(
                'authenticated.myCollection.collectionItemDetails.enabledOffer.actions.seeOffer'
              )}
            </Button>
          </div>
        </>
      )
  const progress =
    status === AutoConfirmationStatus.LOADING
      ? 10
      : status === AutoConfirmationStatus.NEED_ALLOWANCE
      ? 50
      : status === AutoConfirmationStatus.NEED_CONFIRMATION
      ? 75
      : 100
  return (
    <>
      <div className={Styles.root}>
        <div className={Styles.progress}>
          <ProgressBar progress={progress} size={'24px'} mode='gauge' />
        </div>
        <Spacing size='16' />
        <AsyncControl
          control={control}
          onClose={onCancel}
          onRetry={() => control.process(() => doAction()!)}>
          {!control.error && <Text code={label.loading} type={LPTypo.H5} />}
        </AsyncControl>
        {!control.error && progressLabel}
      </div>
    </>
  )
}
